/**
 * ReconnectApp Component
 * Allows participants who have already completed onboarding to reconnect
 * their iOS app (e.g., after deleting and reinstalling the app)
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  TextField,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  PhoneIphone,
  CheckCircle,
  Info,
  Person,
  Email,
  QrCode2,
} from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ParticipantService } from '../../services/participantService';
import LoadingSpinner from '../common/LoadingSpinner';
import QRCodeLinking from './QRCodeLinking';

type ReconnectStep = 'verify' | 'qr_code' | 'success';

const ReconnectApp: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState<ReconnectStep>('verify');

  // Participant info
  const [participantId, setParticipantId] = useState('');
  const [email, setEmail] = useState('');
  const [studyCode, setStudyCode] = useState('');
  const [verifiedParticipant, setVerifiedParticipant] = useState<any>(null);

  // Get participant info from URL params if available
  useEffect(() => {
    const urlParticipantId = searchParams.get('participantId');
    const urlEmail = searchParams.get('email');
    const urlStudyCode = searchParams.get('studyCode');

    if (urlParticipantId) setParticipantId(urlParticipantId);
    if (urlEmail) setEmail(urlEmail);
    if (urlStudyCode) setStudyCode(urlStudyCode);
  }, [searchParams]);

  const handleVerifyParticipant = async () => {
    if (!participantId.trim() || !email.trim()) {
      setError('Please enter both Participant ID and email address');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const verifyResponse = await ParticipantService.verifyParticipant(
        participantId.trim(),
        email.trim().toLowerCase()
      );

      if (!verifyResponse.success || !verifyResponse.data) {
        setError('Participant verification failed. Please check your information.');
        return;
      }

      // Store verified participant data
      setVerifiedParticipant(verifyResponse.data);
      setStudyCode(verifyResponse.data.studyCode);
      setCurrentStep('qr_code');
    } catch (err: any) {
      setError('Verification failed: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLinkingComplete = () => {
    setCurrentStep('success');
  };

  const handleFallbackRequest = () => {
    // Navigate to email code flow if user can't scan QR
    navigate(`/participant/verify-email?participantId=${participantId}&email=${encodeURIComponent(email)}&studyCode=${studyCode}&reconnect=true`);
  };

  const steps = ['Verify Identity', 'Scan QR Code', 'Complete'];

  const getActiveStep = () => {
    switch (currentStep) {
      case 'verify':
        return 0;
      case 'qr_code':
        return 1;
      case 'success':
        return 2;
      default:
        return 0;
    }
  };

  if (loading) {
    return <LoadingSpinner message="Verifying your information..." />;
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <PhoneIphone sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Reconnect iOS App
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Link your StudySync app to continue participating in your study
          </Typography>
        </Box>

        {/* Stepper */}
        <Stepper activeStep={getActiveStep()} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper elevation={3} sx={{ p: 4 }}>
          {/* Step 1: Verify Identity */}
          {currentStep === 'verify' && (
            <>
              {/* Information Card */}
              <Card sx={{ mb: 4, backgroundColor: 'info.light', color: 'info.contrastText' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Info sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      When to Use This Page
                    </Typography>
                  </Box>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: 'info.contrastText' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Reinstalled the App"
                        secondary="If you deleted and reinstalled the StudySync iOS app"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: 'info.contrastText' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="New Device"
                        secondary="If you're setting up the app on a new iPhone"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: 'info.contrastText' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Connection Issues"
                        secondary="If your app is no longer syncing data with the study"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>

              <Typography variant="h6" sx={{ mb: 2 }}>
                Verify Your Identity
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enter your participant information to reconnect your iOS app.
              </Typography>

              <TextField
                fullWidth
                label="Participant ID"
                value={participantId}
                onChange={(e) => setParticipantId(e.target.value)}
                placeholder="P1234567890ABCDEF"
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                helperText="Your participant ID can be found in your enrollment confirmation email"
              />

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                helperText="Use the same email address you used to enroll"
              />

              <Button
                variant="contained"
                size="large"
                startIcon={<QrCode2 />}
                onClick={handleVerifyParticipant}
                disabled={loading || !participantId.trim() || !email.trim()}
                fullWidth
              >
                {loading ? 'Verifying...' : 'Continue to QR Code'}
              </Button>
            </>
          )}

          {/* Step 2: QR Code Linking */}
          {currentStep === 'qr_code' && verifiedParticipant && (
            <>
              <Typography variant="h6" sx={{ mb: 1, textAlign: 'center' }}>
                Scan with Your iPhone
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                Open the StudySync app on your iPhone and scan this QR code
              </Typography>

              <QRCodeLinking
                participantId={participantId}
                studyCode={studyCode}
                email={email}
                onLinkingComplete={handleLinkingComplete}
                onFallbackRequest={handleFallbackRequest}
                onBack={() => setCurrentStep('verify')}
              />
            </>
          )}

          {/* Step 3: Success */}
          {currentStep === 'success' && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                App Successfully Connected!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Your StudySync iOS app is now linked to your account.
                Health data will continue syncing automatically.
              </Typography>

              <Card sx={{ backgroundColor: 'success.light', color: 'success.contrastText', mb: 4 }}>
                <CardContent>
                  <Typography variant="body2">
                    You can close this page and continue using the StudySync app on your iPhone.
                  </Typography>
                </CardContent>
              </Card>

              <Button
                variant="outlined"
                onClick={() => navigate('/')}
              >
                Return to Home
              </Button>
            </Box>
          )}

          {/* Help Section (only show on verify step) */}
          {currentStep === 'verify' && (
            <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Need Help?
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Info color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Can't find your Participant ID?"
                    secondary="Check your enrollment confirmation email or contact the research team"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Info color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Don't have the StudySync app?"
                    secondary="Download it from the App Store before scanning the QR code"
                  />
                </ListItem>
              </List>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ReconnectApp;
